import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axiosMain from '../../services';
import AuthLayout from './AuthLayout';

/**
 * Email links must open the **frontend** app (CloudFront / dev server), not the API host.
 * Set backend `PUBLIC_APP_URL` to your SPA origin so verification URLs load this page.
 */
export default function VerifyEmailTokenPage() {
  const { token } = useParams();
  const [state, setState] = useState({ phase: 'loading', message: '' });

  useEffect(() => {
    const raw = String(token || '').trim();
    if (!raw || raw.length < 32) {
      setState({
        phase: 'error',
        message:
          'This verification link looks invalid. Request a new link from the sign-in page, or confirm the link opens your site (not the API server only).',
      });
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data, status } = await axiosMain.post('/auth/verify-email', { token: raw });
        if (cancelled) return;
        if (status === 200 && data.code === 'ALREADY_VERIFIED') {
          setState({
            phase: 'info',
            message: data.message || 'This email is already verified. You can sign in.',
          });
          return;
        }
        setState({
          phase: 'success',
          message: data.message || 'Email verified successfully. You can sign in.',
        });
      } catch (e) {
        if (cancelled) return;
        const code = e.response && e.response.data && e.response.data.code;
        const msg =
          (e.response && e.response.data && e.response.data.message) ||
          'Verification failed.';
        if (code === 'TOKEN_EXPIRED_OR_INVALID' || code === 'INVALID_TOKEN') {
          setState({
            phase: 'error',
            message: msg,
          });
        } else {
          setState({ phase: 'error', message: msg });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const color =
    state.phase === 'success'
      ? 'green'
      : state.phase === 'error'
        ? 'red'
        : state.phase === 'info'
          ? '#0d47a1'
          : '#555';

  return (
    <AuthLayout title="Email verification">
      {state.phase === 'loading' ? (
        <div className="ace-verify-loading">
          <div className="ace-verify-loading__spinner" aria-hidden="true" />
          <p className="mb-0 mt-2">Verifying your email…</p>
        </div>
      ) : null}
      {state.phase !== 'loading' ? (
        <p style={{ color }}>{state.message}</p>
      ) : null}
      {state.phase === 'error' ? (
        <div className="mt-3">
          <p className="small text-muted mb-2">
            If this page was blank before, the link may point at the API instead of the website, or
            the SPA may not be configured for this path (e.g. CloudFront 403). Set{' '}
            <code>PUBLIC_APP_URL</code> on the server to your <strong>frontend</strong> URL and map
            403/404 to <code>index.html</code>.
          </p>
          <Link to="/verify-email">Request a new verification link</Link>
          {' · '}
          <Link to="/sign-in">Sign in</Link>
        </div>
      ) : null}
      {(state.phase === 'success' || state.phase === 'info') ? (
        <p className="mt-3">
          <Link to="/sign-in">Continue to sign in</Link>
        </p>
      ) : null}
    </AuthLayout>
  );
}
